import { v4 as uuid } from 'uuid';
import { Vector2, Vector3 } from 'three';
import { Element, LinePathElement } from '@thive/canvas';
import { Rectangle, Segment } from '@thive/fddm-types';

import { IdUtils, NumUtils } from '@/modules/common/utils';
import { DrawableToolId, SegmentToolId } from '@/modules/toolbar/types';
import { isSegmentToolId } from '@/modules/toolbar/guards';
import { assertNever } from '@/helpers/utils';
import { logger } from '@common/logging/logger';

import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { Area } from '@/modules/floorplanDesign/types';
import { getName } from '@/modules/floorplanDesign/helpers/name';
import { useFloorplanDesignStore } from '../store/useFloorplanDesignStore';
import { getLoadHandlingType } from './singularStationAreaHelpers';
import { areaFactory } from '../factories';

const createFDDMArea = (
  position: Vector3,
  size: Vector2,
  angleInDeg: number,
  type: DrawableToolId,
  supportedVehicleId: string,
  supportedLoadCarriersId: string,
  loadCarrierWidth: number,
  loadCarrierLength: number,
  name: string,
): Area | undefined => {
  const rectangleMM = RectangleUtils.canvasLeftBottomRectToFDDMRect(
    position.x,
    position.y,
    size.x,
    size.y,
    0,
  );
  const rectangle: Rectangle = {
    centerX: NumUtils.mmToMeters(rectangleMM.centerX),
    centerY: NumUtils.mmToMeters(rectangleMM.centerY),
    width: NumUtils.mmToMeters(rectangleMM.width),
    length: NumUtils.mmToMeters(rectangleMM.length),
    angle: rectangleMM.angle,
  };

  switch (type) {
    case 'highway': {
      const rectangle = RectangleUtils.canvasLeftBottomRectToFDDMRect(
        position.x,
        position.y,
        size.x,
        size.y,
        angleInDeg,
      );
      const id = uuid();
      const segment = {
        ...RectangleUtils.toPartialSegment({
          centerX: rectangle.centerX / 1000,
          centerY: rectangle.centerY / 1000,
          width: rectangle.width / 1000,
          length: rectangle.length / 1000,
          angle: rectangle.angle,
        }),
        id: IdUtils.buildIdWithSegmentIndex(id, 0),
      };
      return areaFactory.buildArea({
        areaType: 'HIGHWAY',
        creationParams: {
          id: uuid(),
          name,
          segments: [segment],
          supportedVehicleId,
        },
      });
    }
    case 'parking':
    case 'charging':
    case 'extraction':
    case 'insertion':
      return areaFactory.buildArea({
        areaType: 'STATIONARY',
        creationParams: {
          id: uuid(),
          name,
          rectangle,
          stationaryType: type.toUpperCase() as 'PARKING' | 'CHARGING' | 'EXTRACTION' | 'INSERTION',
          supportedVehicleId,
        },
      });

    case 'delivery':
    case 'intake':
    case 'process':
    case 'storage':
      return areaFactory.buildArea({
        areaType: 'SINGULAR',
        creationParams: {
          id: uuid(),
          name,
          rectangle,
          loadCarrierDimensions: { length: loadCarrierLength, width: loadCarrierWidth },
          loadHandlingType: getLoadHandlingType(type), // what to do with this
          supportedLoadCarrierId: supportedLoadCarriersId,
          supportedVehicleId,
        },
      });

    case 'racking': {
      return areaFactory.buildArea({
        areaType: 'RACKING',
        creationParams: {
          id: uuid(),
          name,
          rectangle,
          supportedLoadCarrierId: supportedLoadCarriersId,
          supportedVehicleId,
        },
      });
    }
    case 'manual': {
      return areaFactory.buildArea({
        areaType: 'MANUAL',
        creationParams: {
          id: uuid(),
          name,
          rectangle,
          supportedVehicleId,
          supportedLoadCarrierId: supportedLoadCarriersId,
          loadCarrierDimensions: { length: loadCarrierLength, width: loadCarrierWidth },
        },
      });
    }
    case 'highway_angled':
    case 'logical_space':
    case 'obstacle':
    case 'wall':
      logger.error(`${type} is not supported yet`);
      break;
    default:
      assertNever(type);
  }
};

const createSegmentArea = (
  points3d: readonly Vector3[],
  type: SegmentToolId,
  supportedVehicleId: string,
  name: string,
) => {
  const { getPreference } = useFloorplanDesignStore.getState();
  const points = points3d.map((point) => ({
    x: point.x / 1000,
    y: point.y / 1000,
  }));
  const id = uuid();
  const segments: Segment[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    segments.push({
      id: IdUtils.buildIdWithSegmentIndex(id, i),
      leftSideWidth: getPreference('highwayArea.segment.width') / 2,
      rightSideWidth: getPreference('highwayArea.segment.width') / 2,
      start: points[i],
      end: points[i + 1],
      height: 0,
    });
  }

  switch (type) {
    case 'highway_angled': {
      return areaFactory.buildArea({
        areaType: 'HIGHWAY',
        creationParams: {
          id: uuid(),
          name,
          segments: [segments[0], ...segments.slice(1)],
          supportedVehicleId,
        },
      });
    }
    default:
      logger.error(`Can not provide a default: ${type}`);
      return undefined;
  }
};

export const buildAreaFromElement = (
  element: Element,
  type: DrawableToolId,
  supportedVehicleId: string,
  supportedLoadCarriersId: string,
  loadCarrierWidth: number,
  loadCarrierLength: number,
): Area | undefined => {
  const name = getName(type);

  if (element.type === 'Rect') {
    if (!element.position || !element.size || !element.rotation) {
      logger.error('Expected element.position, element.size and element.rotation to be defined');
      return undefined;
    }

    return createFDDMArea(
      element.position,
      element.size,
      element.rotation.z,
      type,
      supportedVehicleId,
      supportedLoadCarriersId,
      loadCarrierWidth,
      loadCarrierLength,
      name,
    );
  }
  if (element.type === 'LinePath' && isSegmentToolId(type)) {
    const linePathElement: LinePathElement = element;
    if (!linePathElement.points) {
      logger.error('Expected element.points to be defined.');
      return undefined;
    }

    return createSegmentArea(linePathElement.points, type, supportedVehicleId, name);
  }
};
